/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ReactHtmlParser, { convertNodeToElement } from 'react-html-parser';
import {
    bool, func, number, object, oneOfType, shape, string,
} from 'prop-types';
import SubscriptionDropDown from './SubscriptionDropDown';
import Checkbox from './Checkbox';
import { getSubscriptionOptions } from '../../../../../helpers/common/subscription/subscription-helpers';

const useStyles = makeStyles((theme) => ({
    module: {
        width: '100%',
        padding: '18px 22px',
        color: '#333',
        fontSize: 16,
        lineHeight: 1.4,
        [theme.breakpoints.down('xs')]: {
            padding: '16px',
            fontSize: 14,
        },
    },
    headingButton: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        padding: 0,
        border: 0,
        background: 'none',
        color: 'inherit',
        font: 'inherit',
        fontSize: '1em',
        fontWeight: 700,
        textAlign: 'left',
        cursor: 'pointer',
    },
    headingIcon: {
        flex: 'none',
        width: 18,
        height: 18,
        objectFit: 'contain',
        marginRight: 8,
    },
    headingText: {
        flex: 1,
    },
    chevron: {
        flex: 'none',
        marginLeft: 12,
    },
    details: {
        marginLeft: 16,
        [theme.breakpoints.down('xs')]: {
            marginLeft: 10,
        },
    },
    bulletPoints: {
        margin: '16px 0 20px',
        paddingLeft: 8,
        fontSize: '1em',
        '& li': {
            marginBottom: 8,
            paddingLeft: 0,
        },
        '& a': {
            color: 'inherit',
            textDecoration: 'underline',
        },
    },
    checkboxRow: {
        display: 'flex',
        alignItems: 'center',
        marginLeft: -16,
        fontSize: '1em',
        [theme.breakpoints.down('xs')]: {
            marginLeft: -10,
        },
    },
    checkboxLabel: {
        fontSize: '1em',
        fontWeight: 700,
        cursor: 'pointer',
    },
    dropdowns: {
        display: 'flex',
        gap: 12,
        margin: '16px 0 0 -16px',
        [theme.breakpoints.down('xs')]: {
            flexDirection: 'column',
            marginLeft: -10,
        },
    },
    subscriptionValues: {
        margin: '16px 0 0 -16px',
        '& dl': {
            display: 'flex',
            gap: 8,
            margin: '4px 0',
        },
        '& dt': {
            color: '#6F675D',
        },
        '& dd': {
            margin: 0,
        },
        [theme.breakpoints.down('xs')]: {
            marginLeft: -10,
        },
    },
    dialogContent: {
        fontSize: 16,
        '& a': {
            textDecoration: 'underline',
        },
    },
    dialogTitle: {
        position: 'relative',
        paddingRight: 56,
    },
    dialogCloseButton: {
        position: 'absolute',
        top: 12,
        right: 12,
        padding: 0,
    },
}));

const CollapsableSubscription = ({
    globalSubscriptionCopy,
    subscriptionDetailsFrequencyValue,
    subscriptionDetailsDurationValue,
    subscriptionConfig,
    isShowingFrequencyDropdown,
    isSubscriptionDurationListEnabled,
    selectValue,
    selectedSku,
    handleChange,
    durationValue,
    subscriptionCheckbox,
    triggerSubscriptionCheck,
}) => {
    const classes = useStyles();
    const [expanded, setExpanded] = useState(true);
    const [openModal, setOpenModal] = useState(null);
    const moduleCopy = globalSubscriptionCopy?.pdp_module || {};
    const subscriptionsList = selectedSku?.subscriptionsList || {};
    const availableTypes = getSubscriptionOptions(subscriptionsList.type);
    const availableIntervals = getSubscriptionOptions(subscriptionsList.interval);
    const availableDurations = getSubscriptionOptions(subscriptionsList.duration);
    const isSubscriptionOnly = availableTypes.length > 0 && availableIntervals.length === 1 && availableDurations.length === 1;
    const frequencyLabel = subscriptionConfig.subscription_interval[availableIntervals[0]] || availableIntervals[0];
    const durationLabel = subscriptionConfig.subscription_duration[availableDurations[0]] || availableDurations[0];

    const renderBulletLink = (node, index) => {
        if (node.type !== 'tag' || node.name !== 'a') return undefined;
        const href = node.attribs?.href || '';
        const modal = href.includes('subscription-cancel-modal') ? 'cancel' : 'terms';
        return (
            <a
                key={`${href}-${index}`}
                href={href}
                onClick={(event) => {
                    event.preventDefault();
                    setOpenModal(modal);
                }}
            >
                {node.children.map((child, childIndex) => convertNodeToElement(child, childIndex))}
            </a>
        );
    };

    let activeModal = null;
    if (openModal === 'cancel') {
        activeModal = {
            title: globalSubscriptionCopy?.cancel_modal_copy?.modal_title,
            copy: globalSubscriptionCopy?.cancel_modal_copy?.modal_copy,
        };
    } else if (openModal === 'terms') {
        activeModal = {
            title: 'Subscription Program Terms and Conditions',
            copy: globalSubscriptionCopy?.subscription_terms,
        };
    }

    return (
        <div className={classes.module} data-testid="pdp-subscription-module">
            <button
                type="button"
                className={classes.headingButton}
                aria-expanded={expanded}
                aria-controls="pdp-subscription-details"
                onClick={() => setExpanded(!expanded)}
            >
                {moduleCopy?.icon?.url && <img className={classes.headingIcon} src={moduleCopy.icon.url} alt="" />}
                <span className={classes.headingText}>{moduleCopy.heading}</span>
                {expanded ? <ExpandLessIcon className={classes.chevron} /> : <ExpandMoreIcon className={classes.chevron} />}
            </button>
            {expanded && (
                <div id="pdp-subscription-details" className={classes.details}>
                    {moduleCopy?.bullet_points?.length > 0 && (
                        <ul className={classes.bulletPoints}>
                            {moduleCopy.bullet_points.map((point) => <li key={point}>{ReactHtmlParser(point, { transform: renderBulletLink })}</li>)}
                        </ul>
                    )}
                    {!isSubscriptionOnly && (
                        <div className={classes.checkboxRow}>
                            <Checkbox
                                id="chk-monthly-subscription"
                                checked={subscriptionCheckbox}
                                onChange={triggerSubscriptionCheck}
                            />
                            <label className={classes.checkboxLabel} htmlFor="chk-monthly-subscription">
                                {moduleCopy.checkbox_label}
                            </label>
                        </div>
                    )}
                    {(subscriptionCheckbox || isSubscriptionOnly) && (
                        isSubscriptionOnly ? (
                            <div className={classes.subscriptionValues} data-testid="subscription-fixed-values">
                                <dl><dt>Frequency:</dt><dd>{frequencyLabel}</dd></dl>
                                <dl><dt>Duration:</dt><dd>{durationLabel}</dd></dl>
                            </div>
                        ) : (
                            <div className={classes.dropdowns}>
                                <SubscriptionDropDown
                                    selectValue={selectValue}
                                    subscriptionsList={subscriptionsList}
                                    fieldLabel="Delivery Frequency"
                                    handleChange={handleChange}
                                    defaultValue={subscriptionConfig.subscription_interval[subscriptionDetailsFrequencyValue] || subscriptionDetailsFrequencyValue}
                                    isShowingDropdown={isShowingFrequencyDropdown}
                                />
                                <SubscriptionDropDown
                                    selectValue={durationValue}
                                    subscriptionsList={subscriptionsList}
                                    handleChange={handleChange}
                                    fieldLabel="Subscription Duration"
                                    dropdownType="subscription_duration"
                                    frequencyOrDuration="duration"
                                    defaultValue={subscriptionConfig.subscription_duration[subscriptionDetailsDurationValue] || subscriptionDetailsDurationValue}
                                    isShowingDropdown={isSubscriptionDurationListEnabled}
                                />
                            </div>
                        )
                    )}
                </div>
            )}
            <Dialog
                open={Boolean(activeModal)}
                onClose={() => setOpenModal(null)}
                aria-labelledby="pdp-subscription-modal-title"
                data-testid="pdp-subscription-modal"
                fullWidth
                maxWidth="md"
            >
                {activeModal && (
                    <>
                        <DialogTitle id="pdp-subscription-modal-title" className={classes.dialogTitle}>
                            {activeModal.title}
                            <IconButton className={classes.dialogCloseButton} aria-label="Close" onClick={() => setOpenModal(null)}>
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent className={classes.dialogContent}>
                            {activeModal.copy && ReactHtmlParser(activeModal.copy)}
                        </DialogContent>
                    </>
                )}
            </Dialog>
        </div>
    );
};

CollapsableSubscription.propTypes = {
    globalSubscriptionCopy: object.isRequired,
    subscriptionDetailsFrequencyValue: oneOfType([number, string]).isRequired,
    subscriptionDetailsDurationValue: oneOfType([number, string]).isRequired,
    subscriptionConfig: object.isRequired,
    isShowingFrequencyDropdown: bool.isRequired,
    isSubscriptionDurationListEnabled: bool.isRequired,
    selectValue: oneOfType([number, string]).isRequired,
    selectedSku: shape({
        subscriptionsList: object.isRequired,
    }).isRequired,
    handleChange: func.isRequired,
    durationValue: oneOfType([number, string]).isRequired,
    subscriptionCheckbox: bool.isRequired,
    triggerSubscriptionCheck: func.isRequired,
};

export default CollapsableSubscription;
